import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Grid,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ArrowBackIosNew,
  Visibility,
  CheckCircle,
  HighlightOff,
  Close,
} from "@mui/icons-material";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import {
  useGetAdminStakingPoolsQuery,
  useApproveAdminStakingPoolMutation,
  useRejectAdminStakingPoolMutation,
} from "../../../redux/api/authApi";
import { resolveInvestorUploadUrl } from "../../investor/myInvestmentsMapper";
import AdminStakingPoolsSkeleton from "./AdminStakingPoolsSkeleton";

const ALL_STATUS = "All Status";
const STATUS_OPTIONS = ["pending", "approved", "rejected"];

function capitalize(s) {
  const str = String(s || "");
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
}

const cardSx = {
  borderRadius: 1.5,
  boxShadow: (theme) => theme.palette.shadow.statCard,
  border: (theme) => `1px solid ${theme.palette.border.light}`,
};

function formatCurrency(num) {
  if (num == null || Number.isNaN(Number(num))) return "—";
  return `SAR ${Number(num).toLocaleString("en-US")}`;
}

function statusChipKey(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "approved" || key === "active") return "success";
  if (key === "rejected") return "error";
  return "pending";
}

function categoryIcon(category) {
  const key = String(category || "").trim().toLowerCase();
  if (key === "real estate") return ApartmentOutlinedIcon;
  if (key === "luxury goods") return DiamondOutlinedIcon;
  return LayersOutlinedIcon;
}

function mapPoolRow(row) {
  const asset = row.assetId && typeof row.assetId === "object" ? row.assetId : {};
  return {
    id: row._id,
    poolName: row.poolName,
    assetId: asset._id,
    assetName: asset.basicInfo?.assetName || "Unnamed asset",
    assetImageUrl: resolveInvestorUploadUrl(asset.assetImages?.mainImage?.fileUrl),
    category: row.category || asset.assetCategory?.name || "",
    annualYield: row.annualYield,
    lockPeriod: row.lockPeriod,
    minStake: row.minStake,
    targetStakeAmount: row.targetStakeAmount,
    totalStaked: row.totalStaked,
    participantsCount: row.participantsCount,
    status: row.status,
    ownerId: row.ownerId,
  };
}

// const KpiCard = ({ title, value, hint, hintColor, icon: Icon, iconBg, iconColor }) => (
//   <Paper elevation={0} sx={{ ...cardSx, p: 2.5, height: "100%" }}>
//     <Box
//       sx={{
//         width: 40,
//         height: 40,
//         borderRadius: 1,
//         bgcolor: iconBg,
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         mb: 2,
//       }}
//     >
//       <Icon sx={{ color: iconColor, fontSize: 22 }} />
//     </Box>
//     <Typography variant="body2" sx={{ color: "text.muted", fontWeight: 500 }}>
//       {title}
//     </Typography>
//     <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary", mt: 0.5 }}>
//       {value}
//     </Typography>
//     <Typography variant="caption" sx={{ color: hintColor, display: "block", mt: 0.5 }}>
//       {hint}
//     </Typography>
//   </Paper>
// );

function ApprovePoolModal({ open, pool, onClose, onConfirm }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open, pool?.id]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(pool);
    } catch (e) {
      setError(e?.data?.message || e?.error || "Could not approve pool.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Typography fontWeight={700} fontSize={18}>
          Approve Staking Pool
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {pool?.poolName}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: "border.subtle" }}>
        <Typography variant="body2" color="text.secondary">
          This pool will become visible to investors and open for staking. Are you sure you want to
          approve it?
        </Typography>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting}
          startIcon={<CheckCircle sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            bgcolor: (theme) => theme.palette.chips.success.text,
            "&:hover": { bgcolor: (theme) => theme.palette.chips.success.text },
          }}
        >
          Approve Pool
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RejectPoolModal({ open, pool, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setValidationError(null);
      setError(null);
    }
  }, [open, pool?.id]);

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setValidationError("Rejection reason is required.");
      return;
    }
    setValidationError(null);
    setError(null);
    setSubmitting(true);
    try {
      await onConfirm(pool, trimmed);
    } catch (e) {
      setError(e?.data?.message || e?.error || "Could not reject pool.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, pr: 6 }}>
        <Typography fontWeight={700} fontSize={18}>
          Reject Staking Pool
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {pool?.poolName}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: "absolute", right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: "border.subtle" }}>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          required
          label="Rejection reason"
          placeholder="Explain why this pool is being rejected..."
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (validationError) setValidationError(null);
          }}
          error={Boolean(validationError)}
          helperText={validationError || " "}
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={submitting}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Reject Pool
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminStakingPools() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ALL_STATUS);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const { data, isLoading, isError, error, refetch } = useGetAdminStakingPoolsQuery();
  const [approvePool] = useApproveAdminStakingPoolMutation();
  const [rejectPool] = useRejectAdminStakingPoolMutation();

  const pools = useMemo(() => {
    const rows = Array.isArray(data?.data) ? data.data : [];
    return rows.map(mapPoolRow);
  }, [data?.data]);

  const filteredPools = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pools.filter((p) => {
      const matchesSearch =
        !q ||
        p.poolName?.toLowerCase().includes(q) ||
        p.assetName?.toLowerCase().includes(q);
      const matchesStatus = status === ALL_STATUS || p.status === status;
      return matchesSearch && matchesStatus;
    });
  }, [pools, search, status]);

  // const kpis = useMemo(() => {
  //   const total = pools.length;
  //   const pending = pools.filter((p) => String(p.status).toLowerCase() === "pending").length;
  //   const approved = pools.filter((p) =>
  //     ["approved", "active"].includes(String(p.status).toLowerCase())
  //   ).length;
  //   const totalStaked = pools.reduce((sum, p) => sum + (Number(p.totalStaked) || 0), 0);
  //   return { total, pending, approved, totalStaked };
  // }, [pools]);

  const openApprove = (pool) => setApproveTarget(pool);
  const closeApprove = () => setApproveTarget(null);

  const handleApproveConfirm = async (pool) => {
    await approvePool({ poolId: pool.id }).unwrap();
    closeApprove();
  };

  const openReject = (pool) => setRejectTarget(pool);
  const closeReject = () => setRejectTarget(null);

  const handleRejectConfirm = async (pool, reason) => {
    await rejectPool({ poolId: pool.id, rejectionReason: reason }).unwrap();
    closeReject();
  };

  if (isLoading) {
    return <AdminStakingPoolsSkeleton />;
  }

  return (
    <Box>
      <Button
        onClick={() => navigate("/admin")}
        startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
        sx={{
          textTransform: "none",
          color: "text.primary",
          mb: 2,
          px: 0,
          "&:hover": { backgroundColor: "transparent" },
        }}
      >
        Back to Dashboard
      </Button>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 1.5,
            bgcolor: "background.headerIcon",
            border: (theme) => `1px solid ${theme.palette.border.light}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <SavingsRoundedIcon sx={{ color: "text.slate", fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: "text.primary", letterSpacing: -0.5 }}>
            Staking Pools
          </Typography>
          <Typography variant="body2" sx={{ color: "text.muted", mt: 0.5 }}>
            Review staking pools listed by asset owners across the platform.
          </Typography>
        </Box>
      </Stack>

      {isError ? (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => refetch()}>
          {error?.data?.message || error?.error || "Failed to load staking pools."}
        </Alert>
      ) : null}

      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Total Pools"
            value={String(kpis.total)}
            hint="Across all owners"
            hintColor={(theme) => theme.palette.statIcon.revenue.color}
            icon={DescriptionOutlined}
            iconBg={(theme) => theme.palette.statIcon.revenue.bg}
            iconColor={(theme) => theme.palette.statIcon.revenue.color}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Pending Review"
            value={String(kpis.pending)}
            hint="Awaiting approval"
            hintColor={(theme) => theme.palette.chips.pending.text}
            icon={AccessTime}
            iconBg={(theme) => theme.palette.chips.pending.bg}
            iconColor={(theme) => theme.palette.chips.pending.text}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Approved"
            value={String(kpis.approved)}
            hint="Live for investors"
            hintColor={(theme) => theme.palette.chips.success.text}
            icon={TaskAlt}
            iconBg={(theme) => theme.palette.chips.success.bg}
            iconColor={(theme) => theme.palette.chips.success.text}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiCard
            title="Total Staked"
            value={formatCurrency(kpis.totalStaked)}
            hint="Across all pools"
            hintColor={(theme) => theme.palette.statIcon.users.color}
            icon={SavingsRoundedIcon}
            iconBg={(theme) => theme.palette.statIcon.users.bg}
            iconColor={(theme) => theme.palette.statIcon.users.color}
          />
        </Grid>
      </Grid> */}

      <Paper elevation={0} sx={{ ...cardSx, p: 3, mb: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by pool or asset name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" sx={{ color: "text.muted" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.25 } }}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} sx={{ borderRadius: 1.25 }}>
              <MenuItem value={ALL_STATUS}>{ALL_STATUS}</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {capitalize(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {filteredPools.length === 0 ? (
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 4, sm: 8 }, textAlign: "center" }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "background.activityIcon",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 3,
            }}
          >
            <LayersOutlinedIcon sx={{ fontSize: 34, color: (theme) => theme.palette.stats.indigo }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}>
            No staking pools found
          </Typography>
          <Typography variant="body2" sx={{ color: "text.muted", maxWidth: 420, mx: "auto" }}>
            {pools.length === 0
              ? "Once asset owners list staking pools, they will show up here for review."
              : "No pools match your current filters."}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          {filteredPools.map((pool) => {
            const CategoryIcon = categoryIcon(pool.category);
            const chipKey = statusChipKey(pool.status);
            return (
              <Box
                key={pool.id}
                sx={{
                  p: 3,
                  borderRadius: 1.5,
                  bgcolor: (theme) => theme.palette.background.paper,
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "flex-start" }}
                  spacing={2}
                  mb={3}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    {!pool.assetImageUrl && (
                      <Box
                        sx={{
                          borderRadius: 1.25,
                          bgcolor: "background.paper",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CategoryIcon sx={{ color: (theme) => theme.palette.accent.teal.chipText, fontSize: 28 }} />
                      </Box>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 16, fontWeight: 700, color: "text.primary" }}>
                        {pool.poolName}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                        {pool.category ? (
                          <Chip
                            label={`${pool.category} Staking`}
                            size="small"
                            sx={{
                              bgcolor: (theme) => theme.palette.primary.main,
                              color: "common.white",
                              fontWeight: 600,
                              fontSize: 11,
                              height: 22,
                              borderRadius: 999,
                            }}
                          />
                        ) : null}
                        <Chip
                          label={pool.status}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: "capitalize",
                            borderRadius: 999,
                            bgcolor: (theme) => theme.palette.chips[chipKey].bg,
                            color: (theme) => theme.palette.chips[chipKey].text,
                          }}
                        />
                      </Stack>
                      <Typography variant="body2" sx={{ color: "text.muted", mt: 0.75 }}>
                        {pool.assetName}
                      </Typography>
                      {/* <Typography variant="caption" sx={{ color: "text.faint" }}>
                        Owner ID: {shortId(pool.ownerId)}
                      </Typography> */}
                    </Box>
                  </Stack>

                  <Stack
                    direction={{ xs: "row", sm: "column" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "center", sm: "flex-end" }}
                    spacing={1}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
                      <Typography sx={{ fontSize: 12, color: "text.muted" }}>Annual Yield</Typography>
                      <Typography
                        sx={{
                          fontSize: 28,
                          fontWeight: 800,
                          color: (theme) => theme.palette.accent.teal.chipText,
                          letterSpacing: -0.5,
                        }}
                      >
                        {pool.annualYield}%
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility sx={{ fontSize: 16 }} />}
                        onClick={() => pool.assetId && navigate(`/admin/assets/${pool.assetId}`)}
                        disabled={!pool.assetId}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 1.25,
                          borderColor: "border.light",
                          color: "text.primary",
                          bgcolor: "background.paper",
                        }}
                      >
                        View Asset
                      </Button>
                      {String(pool.status).toLowerCase() === "pending" && (
                        <>
                          <Tooltip title="Approve pool" arrow>
                            <IconButton
                              size="small"
                              onClick={() => openApprove(pool)}
                              aria-label="Approve pool"
                              sx={{
                                color: (theme) => theme.palette.chips.success.text,
                                bgcolor: (theme) => theme.palette.chips.success.bg,
                                "&:hover": {
                                  bgcolor: (theme) => theme.palette.chips.success.bg,
                                },
                              }}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject pool" arrow>
                            <IconButton
                              size="small"
                              onClick={() => openReject(pool)}
                              aria-label="Reject pool"
                              sx={{
                                color: (theme) => theme.palette.chips.error.text,
                                bgcolor: (theme) => theme.palette.chips.error.bg,
                                "&:hover": {
                                  bgcolor: (theme) => theme.palette.chips.error.bg,
                                },
                              }}
                            >
                              <HighlightOff fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Stack>

                <Grid container spacing={3}>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <Typography sx={{ fontSize: 12, color: "text.muted" }}>Lock Period</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
                      <LockOutlinedIcon sx={{ fontSize: 14, color: "text.muted" }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>
                        {pool.lockPeriod} days
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <Typography sx={{ fontSize: 12, color: "text.muted" }}>Min Stake</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 0.25 }}>
                      {formatCurrency(pool.minStake)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography sx={{ fontSize: 12, color: "text.muted" }}>Target Stake Amount</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 0.25 }}>
                      {formatCurrency(pool.targetStakeAmount)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Typography sx={{ fontSize: 12, color: "text.muted" }}>Total Staked</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 0.25 }}>
                      {formatCurrency(pool.totalStaked)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <Typography sx={{ fontSize: 12, color: "text.muted" }}>Participants</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5} mt={0.25}>
                      <GroupOutlinedIcon sx={{ fontSize: 14, color: "text.muted" }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>
                        {pool.participantsCount ?? 0}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </Stack>
      )}

      <ApprovePoolModal
        open={Boolean(approveTarget)}
        pool={approveTarget}
        onClose={closeApprove}
        onConfirm={handleApproveConfirm}
      />

      <RejectPoolModal
        open={Boolean(rejectTarget)}
        pool={rejectTarget}
        onClose={closeReject}
        onConfirm={handleRejectConfirm}
      />
    </Box>
  );
}
